import React from "react";
import { LegalPage } from "@/pages/LegalPage";

const SECTIONS = [
  {
    h: "The short version",
    p: [
      "We collect your email address if you hand it to us, we use it to send you the things you asked for, and we don't sell it to anybody. Everything below is the longer, more careful version of that sentence.",
    ],
  },
  {
    h: "Who we are",
    p: [
      "Dan Berry Music — Dan Berry, pianist, improviser and educator — operating from Broward County, Florida, USA. This one site covers performances and bookings, private and community events, commissions, soundbaths, the Alephonics podcast, and patronage. The Alephonics method itself and its app now live separately at jewishmusiclab.com.",
      "For anything to do with your data, write to hello@danberrymusic.com. Mail sent to the old Alephonics address still reaches us.",
    ],
  },
  {
    h: "What we collect",
    p: [
      "Your email address, and your first name if you give it, when you subscribe to updates, ask to be told when Dan tours near you, send a message through a contact form, or unlock the Alephonics Try It demo.",
      "Where you are, when you tell us — usually a city, state and country chosen from a suggestion list — so we can route booking enquiries and tour alerts sensibly. Booking enquiries may also include a date you have in mind and which kind of event it is.",
      "Whether you described yourself as an audience member or as a community leader, so we send you the right things and not the wrong ones.",
      "The Hebrew words and verses you enter into the demo or the studio. We may keep these to understand what people are exploring and to improve the mapping — they are not published with identifying detail attached.",
      "If you create an account for the Hebrew studio: your email address, a password stored only as a one-way hash (or a Google account identifier if you sign in with Google), your name if you give it, and the compositions you choose to save.",
      "At a live interactive show, the word, name or request you send in from your phone, and a display name if you enter one. Requests may appear on the screen in front of the room — please don't submit anything you wouldn't want read aloud.",
      "If you become a patron, your name, email and the tier you chose, held by our checkout and email provider. Your first name and last initial appear on the public patrons wall while your patronage is active.",
      "Standard technical information that any website receives: IP address, browser and device type, pages viewed, and referring links.",
    ],
  },
  {
    h: "What we do with it",
    p: [
      "Send you the emails you asked for and reply when you write to us about a booking, a commission or anything else.",
      "Answer booking and commission enquiries, quote for them, and plan tours around where people actually are.",
      "Run your account and your saved compositions in the Hebrew studio, and run live shows in the room.",
      "Handle patronage: billing, thanking you, and showing your name on the wall while it's active.",
      "Run and improve the site: seeing which pages people read, which words they try, what breaks.",
      "We do not sell your personal information, and we do not rent or trade our email list.",
    ],
  },
  {
    h: "Cookies, analytics and tracking",
    p: [
      "We use cookies and similar technologies, both our own and from third parties. Our own do the plumbing: keeping you signed in, remembering that you've already unlocked the demo, and remembering that you dismissed the announcement bar at the top of the page.",
      "We also use Google Analytics, the Meta (Facebook) pixel, and Systeme.io's tracking to understand how people find the site and which pages hold their attention. These services set their own cookies and may use that information for their own purposes, including advertising measurement — their privacy policies govern that use.",
      "You can block or clear cookies in your browser at any time. Some things, like staying signed in, will stop working if you do.",
    ],
  },
  {
    h: "Who else touches your data",
    p: [
      "Systeme.io holds our email list, sends our emails, and runs the patronage checkout. When you subscribe or become a patron, your email address, any location you gave us, and tags describing why you're on the list are stored with them.",
      "Stripe and/or Systeme.io process payments. We never see or store your full card number — the payment processor handles that end to end.",
      "Google provides sign-in for anyone who chooses it, and analytics for the site.",
      "Meta provides the advertising pixel described above.",
      "Sefaria supplies the Torah text used on the site. Requests for that text go through our server, so Sefaria does not receive information about you.",
      "YouTube hosts our podcast videos. A page with an embedded video loads content from YouTube, and YouTube may set cookies and record that a video was viewed.",
      "Our hosting, database and file-storage providers store the site, its data and any images we upload on our behalf.",
    ],
  },
  {
    h: "How long we keep it",
    p: [
      "Email addresses stay on the list until you unsubscribe or ask us to delete them. Accounts and saved compositions stay until you ask us to remove them. Demo entries, live-show submissions and technical logs are kept only as long as they're useful — generally a matter of months. Records tied to patronage or a paid commission are kept as long as tax and accounting rules require.",
    ],
  },
  {
    h: "Your choices",
    p: [
      "Every email we send has an unsubscribe link, and it works immediately.",
      "Write to hello@danberrymusic.com and you can ask us for a copy of what we hold about you, ask us to correct it, or ask us to delete it — including taking your name off the patrons wall. We'll do it, and we won't make it difficult. Depending on where you live you may have these rights by law; we extend them to everyone regardless.",
      "We don't sell personal information, so there is nothing to opt out of on that front.",
    ],
  },
  {
    h: "Children",
    p: [
      "This isn't a site built for children, and we don't knowingly collect information from anyone under 13. If a child has sent us something, write to us and we'll remove it. Children are very welcome at the concerts.",
    ],
  },
  {
    h: "Where your data lives",
    p: [
      "We operate from the United States and our providers are largely US-based, so if you're writing to us from elsewhere your information will be processed in the US.",
    ],
  },
  {
    h: "Security, honestly stated",
    p: [
      "We use encrypted connections, store passwords only as one-way hashes, keep credentials out of the codebase, and limit who can reach the admin side of the site. No website can promise perfect security, and we won't pretend otherwise. What we can promise is that we collect as little as we reasonably can, which is the best protection there is.",
    ],
  },
  {
    h: "Changes",
    p: [
      "If this policy changes in a way that matters, we'll update the date at the top and, for anything significant, say so in an email. This version reflects Alephonics and Dan Berry Music becoming one site.",
    ],
  },
];

export default function Privacy() {
  return (
    <LegalPage
      active="/privacy"
      eyebrow="Privacy"
      title="Privacy Policy"
      intro="One site now covers the performances, the bookings, the commissions and Alephonics. This is the plain-English account of what we collect, why, and what you can do about it."
      sections={SECTIONS}
    />
  );
}
